import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Plus, Edit2, Trash2, Upload, Lock, User, AlertTriangle, Eye, EyeOff, CalendarPlus, FileText } from 'lucide-react';
import { pets as petsApi, users as usersApi } from '../utils/api';
import DynamicFormFields from '../components/DynamicFormFields';
import ClientDocumentsPanel from '../components/ClientDocumentsPanel';
import useSubjectSettings from '../hooks/useSubjectSettings';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Profile = () => {
  const { isEnabled: subjectsEnabled, pluralName: subjectPluralName, singularName: subjectSingularName, icon: subjectIcon } = useSubjectSettings();
  const { user: authUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  const [subjectFields, setSubjectFields] = useState([]);
  const [formData, setFormData] = useState({});
  
  // User profile state
  const [userProfile, setUserProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [editingProfile, setEditingProfile] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [showDeletePw, setShowDeletePw] = useState(false);

  useEffect(() => {
    loadUserProfile();
    if (subjectsEnabled) {
      loadPets();
      loadSubjectFields();
    } else {
      setLoading(false);
    }
  }, [subjectsEnabled]);


  const loadUserProfile = async () => {
    try {
      const response = await usersApi.getProfile();
      const customData = response.data.custom_data || {};
      setUserProfile({
        first_name: response.data.first_name,
        last_name: response.data.last_name,
        email: response.data.email,
        phone: response.data.phone,
        notes: response.data.notes || '',
        ...customData
      });
    } catch (error) {
      toast.error('Failed to load profile');
    }
  };

  const loadPets = async () => {
    try {
      const response = await petsApi.getAll();
      setPets(response.data);
    } catch (error) {
      toast.error('Failed to load pets');
    } finally {
      setLoading(false);
    }
  };

  const loadSubjectFields = async () => {
    try {
      const response = await axios.get(`${API_URL}/subject-fields/active`);
      setSubjectFields(response.data);
    } catch (error) {
      console.error('Failed to load subject fields:', error);
    }
  };

  const handleProfileChange = (newData) => {
    setUserProfile(newData);
  };

  const handleSaveProfile = async () => {
    try {
      await usersApi.updateProfile(userProfile);
      toast.success('Profile updated successfully');
      setEditingProfile(false);
      
      // Update session storage
      const user = JSON.parse(sessionStorage.getItem('user'));
      user.first_name = userProfile.firstName;
      user.last_name = userProfile.lastName;
      user.email = userProfile.email;
      sessionStorage.setItem('user', JSON.stringify(user));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update profile');
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    try {
      await usersApi.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      toast.success('Password changed successfully');
      setShowPasswordModal(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to change password');
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    
    if (!deletePassword) {
      toast.error('Please enter your password to confirm deletion');
      return;
    }

    setDeleting(true);
    try {
      await usersApi.deleteAccount(deletePassword);
      toast.success('Account deleted successfully');
      // Clear session and redirect to login
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      window.location.href = '/login';
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete account');
      setDeleting(false);
    }
  };

  const handleOpenModal = async (pet = null) => {
    // Load subject fields first
    await loadSubjectFields();
    
    if (pet) {
      setEditingPet(pet);
      // Populate form with existing pet data
      const data = {};
      subjectFields.forEach(field => {
        data[field.field_name] = pet[field.field_name] || pet.custom_data?.[field.field_name] || '';
      });
      setFormData(data);
    } else {
      setEditingPet(null);
      // Initialize empty form
      const data = {};
      subjectFields.forEach(field => {
        data[field.field_name] = '';
      });
      setFormData(data);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPet(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingPet) {
        await petsApi.update(editingPet.id, formData);
        toast.success('Pet updated successfully');
      } else {
        await petsApi.create(formData);
        toast.success('Pet added successfully');
      }
      loadPets();
      handleCloseModal();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save pet');
    }
  };

  const handleImageUpload = async (petId, file) => {
    try {
      await petsApi.uploadImage(petId, file);
      toast.success('Image uploaded successfully');
      loadPets();
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const handleDelete = async (petId) => {
    if (!window.confirm('Are you sure you want to delete this pet?')) return;

    try {
      await petsApi.delete(petId);
      toast.success('Pet deleted successfully');
      loadPets();
    } catch (error) {
      toast.error('Failed to delete pet');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Profile & Settings</h1>
          <Link
            to="/book"
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            <CalendarPlus size={18} />
            <span>Book Appointment</span>
          </Link>
        </div>
        
        {/* Tabs */}
        <div className="flex space-x-1 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'profile'
                ? 'border-b-2 border-primary-600 text-primary-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <User className="inline mr-2" size={18} />
            My Profile
          </button>
          {subjectsEnabled && (
            <button
              onClick={() => setActiveTab('pets')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'pets'
                  ? 'border-b-2 border-primary-600 text-primary-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {subjectIcon} My {subjectPluralName}
            </button>
          )}
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'documents'
                ? 'border-b-2 border-primary-600 text-primary-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <FileText className="inline mr-2" size={18} />
            My Documents
          </button>
        </div>

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="max-w-4xl">
            {authUser?.id ? (
              <ClientDocumentsPanel
                clientId={authUser.id}
                viewerRole="client"
              />
            ) : (
              <div className="card text-center text-gray-500 py-8">
                Loading…
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="max-w-2xl">
            <div className="card mb-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Personal Information</h2>
                {!editingProfile ? (
                  <button
                    onClick={() => setEditingProfile(true)}
                    className="btn btn-secondary flex items-center space-x-2"
                  >
                    <Edit2 size={16} />
                    <span>Edit</span>
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setEditingProfile(false);
                        loadUserProfile();
                      }}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveProfile}
                      className="btn btn-primary"
                    >
                      Save Changes
                    </button>
                  </div>
                )}
              </div>

              <DynamicFormFields
                formData={userProfile}
                onChange={handleProfileChange}
                disabled={!editingProfile}
              />
            </div>

            <div className="card">
              <h2 className="text-xl font-bold mb-4">Security</h2>
              <button
                onClick={() => setShowPasswordModal(true)}
                className="btn btn-secondary flex items-center space-x-2"
              >
                <Lock size={16} />
                <span>Change Password</span>
              </button>
            </div>

            <div className="card border-red-200 bg-red-50">
              <h2 className="text-xl font-bold mb-2 text-red-700">Danger Zone</h2>
              <p className="text-sm text-red-600 mb-4">
                Once you delete your account, there is no going back. All your data including pets and appointment history will be permanently removed.
              </p>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="btn bg-red-600 text-white hover:bg-red-700 flex items-center space-x-2"
              >
                <Trash2 size={16} />
                <span>Delete Account</span>
              </button>
            </div>
          </div>
        )}

        {/* Pets Tab */}
        {activeTab === 'pets' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">My {subjectPluralName}</h2>
              <button onClick={() => handleOpenModal()} className="btn btn-primary flex items-center space-x-2">
                <Plus size={20} />
                <span>Add {subjectSingularName}</span>
              </button>
            </div>

        {pets.length === 0 ? (
          <div className="card text-center py-12">
            <div className="text-6xl mb-4">{subjectIcon}</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No {subjectPluralName.toLowerCase()} yet</h2>
            <p className="text-gray-600 mb-6">Add your first {subjectSingularName.toLowerCase()} to start booking appointments</p>
            <button onClick={() => handleOpenModal()} className="btn btn-primary">
              Add Your First {subjectSingularName}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pets.map((pet) => (
              <div key={pet.id} className="card">
                <div className="relative mb-4">
                  {pet.image_url ? (
                    <img 
                      src={pet.image_url.startsWith('http') ? pet.image_url : `${API_URL.replace('/api', '')}${pet.image_url}`} 
                      alt={pet.name} 
                      className="w-full h-48 object-cover rounded-lg"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Ctext x="50%25" y="50%25" font-size="48" text-anchor="middle" dy=".3em"%3E🐕%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  ) : (
                    <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center text-6xl">
                      🐕
                    </div>
                  )}
                  <label className="absolute bottom-2 right-2 bg-white rounded-full p-2 shadow-lg cursor-pointer hover:bg-gray-100">
                    <Upload size={20} className="text-gray-600" />
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(pet.id, e.target.files[0])}
                    />
                  </label>
                </div>

                <h3 className="text-xl font-bold mb-2">{pet.name}</h3>
                <div className="space-y-1 text-sm text-gray-600 mb-4">
                  {pet.breed && <p>Breed: {pet.breed}</p>}
                  {subjectFields.some(f => f.field_label.toLowerCase().includes('size')) && pet.size && <p className="capitalize">Size: {pet.size.replace('_', ' ')}</p>}
                  {pet.age && <p>Age: {pet.age} years</p>}
                  {pet.weight && <p>Weight: {pet.weight} lbs</p>}
                  {pet.special_notes && (
                    <p className="text-xs mt-2 p-2 bg-yellow-50 rounded">
                      Note: {pet.special_notes}
                    </p>
                  )}
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleOpenModal(pet)}
                    className="flex-1 btn btn-secondary flex items-center justify-center space-x-1"
                  >
                    <Edit2 size={16} />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => handleDelete(pet.id)}
                    className="flex-1 btn bg-red-100 text-red-600 hover:bg-red-200 flex items-center justify-center space-x-1"
                  >
                    <Trash2 size={16} />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
          </div>
        )}

        {/* Pet Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6">
                {editingPet ? `Edit ${subjectSingularName}` : `Add New ${subjectSingularName}`}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                {subjectFields.map((field) => {
                  if (field.field_type === 'select') {
                    return (
                      <div key={field.id}>
                        <label className="label">
                          {field.field_label} {field.is_required && '*'}
                        </label>
                        <select
                          name={field.field_name}
                          value={formData[field.field_name] || ''}
                          onChange={handleChange}
                          required={field.is_required}
                          className="input"
                        >
                          <option value="">Select {field.field_label}</option>
                          {field.options?.map((opt, idx) => (
                            <option key={idx} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  }

                  if (field.field_type === 'textarea') {
                    return (
                      <div key={field.id}>
                        <label className="label">
                          {field.field_label} {field.is_required && '*'}
                        </label>
                        <textarea
                          name={field.field_name}
                          value={formData[field.field_name] || ''}
                          onChange={handleChange}
                          required={field.is_required}
                          placeholder={field.placeholder || ''}
                          rows={3}
                          className="input"
                        />
                      </div>
                    );
                  }

                  if (field.field_type === 'checkbox') {
                    const cleanOptions = (field.options || []).filter(o => typeof o === 'string' && o.trim() !== '');
                    if (cleanOptions.length === 0) {
                      return (
                        <div key={field.id}>
                          <label className="label">
                            {field.field_label} {field.is_required && '*'}
                          </label>
                          <input
                            type="text"
                            name={field.field_name}
                            value={formData[field.field_name] || ''}
                            onChange={handleChange}
                            placeholder={field.placeholder || ''}
                            className="input"
                          />
                        </div>
                      );
                    }
                    const selected = Array.isArray(formData[field.field_name]) ? formData[field.field_name] : [];
                    const isOther = (opt) => typeof opt === 'string' && opt.toLowerCase() === 'other';
                    const otherChecked = selected.some((v) => typeof v === 'string' && (v.toLowerCase() === 'other' || v.toLowerCase().startsWith('other:')));
                    const otherText = (selected.find((v) => typeof v === 'string' && v.toLowerCase().startsWith('other:')) || '').replace(/^other:\s*/i, '');

                    const setSelected = (next) => setFormData(prev => ({ ...prev, [field.field_name]: next }));
                    const toggleOption = (option, checked) => {
                      const next = checked ? [...selected, option] : selected.filter(v => v !== option);
                      setSelected(next);
                    };
                    const toggleOther = (checked) => {
                      const cleaned = selected.filter((v) => !(typeof v === 'string' && (v.toLowerCase() === 'other' || v.toLowerCase().startsWith('other:'))));
                      setSelected(checked ? [...cleaned, 'Other'] : cleaned);
                    };
                    const updateOtherText = (text) => {
                      const cleaned = selected.filter((v) => !(typeof v === 'string' && (v.toLowerCase() === 'other' || v.toLowerCase().startsWith('other:'))));
                      setSelected([...cleaned, text.trim() ? `Other: ${text}` : 'Other']);
                    };

                    return (
                      <div key={field.id}>
                        <label className="label">
                          {field.field_label} {field.is_required && '*'}
                        </label>
                        <div className="space-y-2">
                          {cleanOptions.filter(o => !isOther(o)).map((option, idx) => (
                            <label key={idx} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={selected.includes(option)}
                                onChange={(e) => toggleOption(option, e.target.checked)}
                                className="rounded"
                              />
                              <span className="text-sm">{option}</span>
                            </label>
                          ))}
                          <div>
                            <label className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={otherChecked}
                                onChange={(e) => toggleOther(e.target.checked)}
                                className="rounded"
                              />
                              <span className="text-sm">Other</span>
                            </label>
                            {otherChecked && (
                              <input
                                type="text"
                                value={otherText}
                                onChange={(e) => updateOtherText(e.target.value)}
                                placeholder="Please specify..."
                                className="input mt-2 ml-6"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Default: text/number/email/tel inputs
                  return (
                    <div key={field.id}>
                      <label className="label">
                        {field.field_label} {field.is_required && '*'}
                      </label>
                      <input
                        type={field.field_type}
                        name={field.field_name}
                        value={formData[field.field_name] || ''}
                        onChange={handleChange}
                        required={field.is_required}
                        placeholder={field.placeholder || ''}
                        className="input"
                      />
                    </div>
                  );
                })}

                <div className="flex space-x-3 pt-4">
                  <button type="button" onClick={handleCloseModal} className="flex-1 btn btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 btn btn-primary">
                    {editingPet ? `Update ${subjectSingularName}` : `Add ${subjectSingularName}`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Password Change Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <h2 className="text-2xl font-bold mb-6">Change Password</h2>

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="label">Current Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type={showCurrentPw ? 'text' : 'password'}
                      name="currentPassword"
                      required
                      className="input pl-10 pr-10"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                    />
                    <button type="button" onMouseDown={() => setShowCurrentPw(true)} onMouseUp={() => setShowCurrentPw(false)} onMouseLeave={() => setShowCurrentPw(false)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 select-none" tabIndex={-1} title="Hold to reveal">{showCurrentPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                  </div>
                </div>

                <div>
                  <label className="label">New Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type={showNewPw ? 'text' : 'password'}
                      name="newPassword"
                      required
                      minLength="6"
                      className="input pl-10 pr-10"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                    />
                    <button type="button" onMouseDown={() => setShowNewPw(true)} onMouseUp={() => setShowNewPw(false)} onMouseLeave={() => setShowNewPw(false)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 select-none" tabIndex={-1} title="Hold to reveal">{showNewPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">At least 6 characters</p>
                </div>

                <div>
                  <label className="label">Confirm New Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type={showConfirmPw ? 'text' : 'password'}
                      name="confirmPassword"
                      required
                      className="input pl-10 pr-10"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                    />
                    <button type="button" onMouseDown={() => setShowConfirmPw(true)} onMouseUp={() => setShowConfirmPw(false)} onMouseLeave={() => setShowConfirmPw(false)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 select-none" tabIndex={-1} title="Hold to reveal">{showConfirmPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    }}
                    className="flex-1 btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 btn btn-primary">
                    Change Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Account Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-red-100 p-3 rounded-full">
                  <AlertTriangle className="text-red-600" size={24} />
                </div>
                <h2 className="text-2xl font-bold text-red-700">Delete Account</h2>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-700 font-medium mb-2">This action cannot be undone!</p>
                <p className="text-sm text-red-600">
                  Deleting your account will permanently remove:
                </p>
                <ul className="text-sm text-red-600 list-disc list-inside mt-2">
                  <li>Your profile information</li>
                  <li>All your pets</li>
                  <li>Your appointment history</li>
                </ul>
              </div>

              <form onSubmit={handleDeleteAccount} className="space-y-4">
                <div>
                  <label className="label">Enter your password to confirm *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type={showDeletePw ? 'text' : 'password'}
                      required
                      className="input pl-10 pr-10"
                      placeholder="Your current password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                    />
                    <button type="button" onMouseDown={() => setShowDeletePw(true)} onMouseUp={() => setShowDeletePw(false)} onMouseLeave={() => setShowDeletePw(false)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 select-none" tabIndex={-1} title="Hold to reveal">{showDeletePw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setDeletePassword('');
                    }}
                    className="flex-1 btn btn-secondary"
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="flex-1 btn bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting...' : 'Delete My Account'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
